import { isBrowser, globalVar } from 'app/infrastructure/universal';
import { AppSetting } from 'dependencies/appsettings';
import { externalRedirect } from 'app/infrastructure/platform';
import { SigninRequestSettings } from './types';
import { logoutRoute } from './constants';
import { clearCachedToken, cacheAccessToken } from 'app/infrastructure/auth/token-cache';
import { cacheIdToken, getIdToken } from 'app/infrastructure/auth';
import { UserManager } from 'oidc-client';
import { isVeracityEnabled } from 'app/infrastructure/hosting';
import { debug } from 'app/infrastructure/log';

let user: Oidc.User;
let userManager: Oidc.UserManager;

// prevent paralell requests triggering multiple redirects to auth provider
let redirectStarted: boolean;
export const isRedirectStarted = () => redirectStarted;

const logoutPath = (applicationUrl: string) => {
  return (applicationUrl.lastIndexOf('/') === applicationUrl.length - 1
    ? applicationUrl.slice(0, -1) : applicationUrl)
    + logoutRoute;
};

function createAuthCodeSettings(): Oidc.UserManagerSettings {
  return {
    authority: AppSetting.OidcServiceUrl,
    client_id: AppSetting.OidcClientId,
    // Due to a quirk in IdentityServer3, it expects the client secret even for public clients with PKCE and code.
    // Despite what the name implies, it is not a secret value since this public client can't keep a secret.
    client_secret: isVeracityEnabled() ? undefined : 'publicclient', 
    redirect_uri: AppSetting.ApplicationUrl,
    post_logout_redirect_uri: logoutPath(AppSetting.ApplicationUrl),
    scope: AppSetting.OidcScope,
    response_type: 'code',
    response_mode: 'query',
    monitorSession: false,
    automaticSilentRenew: false,
    filterProtocolClaims: true,
    loadUserInfo: !AppSetting.OidcDontLoadUserInfo
  };
}

function createUserManager(): Oidc.UserManager {
  const settings = createAuthCodeSettings();
  return new UserManager(settings);
}

export function initialize() {
  if (!isBrowser || userManager) {
    return;
  }

  userManager = createUserManager();
  userManager.clearStaleState();

  userManager.events.addAccessTokenExpiring(
    async () => {
      debug('Access token expiring');
      if(!await renewToken()) {
        debug('Failed silent sign in. Redirecting...');
        reAuthenticate();
      }
    }
  );

}

export const routerPath = (href: string, hostingPath: string) => {
  if (href === undefined || href === null) {
    return '/';
  }

  const hostPosition = href.indexOf('://') !== -1 ? href.indexOf('://') + 3 : 0;
  const locationPosition = href.indexOf('/', hostPosition);
  const locationPath = locationPosition !== -1 ? href.substring(locationPosition) : '/';

  if (hostingPath === undefined || hostingPath === null || hostingPath.length === 0) {
    return locationPath;
  }

  const hostingPathPosition = locationPath.toLowerCase().indexOf(hostingPath.toLowerCase());
  const hostingPathLength = hostingPath.length - (hostingPath.lastIndexOf('/') === hostingPath.length - 1 ? 1 : 0);
  return hostingPathPosition === -1 || locationPath.length === hostingPathPosition + hostingPathLength
    ? '/'
    : locationPath.substring(hostingPathPosition + hostingPathLength);
};

export function reAuthenticate() {
  if (redirectStarted) {
    return;
  }
  redirectStarted = true;
  clearCachedToken();
  signin(true, true);
}

async function renewToken(): Promise<boolean> {
  try {
    debug('Silent sign in');
    user = await userManager.signinSilent();  
    clearCachedToken();
    cacheAccessToken(user.access_token);
    cacheIdToken(user.id_token);
    return true;
  } catch(err) {
    return false;
  }
}

export async function signin(logAsDifferentUser: boolean = false, storeCurrentRouterPath: boolean = true) {

  initialize();

  const path = storeCurrentRouterPath
    ? routerPath(globalVar('location').href, AppSetting.HostingPath)
    : '/';

  const settings: SigninRequestSettings = {
    state: { redirectUrl: path }
  };

  const providerId = AppSetting.OidcDefaultProviderId;

  if (logAsDifferentUser) {
    settings.prompt = 'login';
  } else if (providerId) {
    settings.acr_values = 'idp:' + providerId;
  }

  await userManager.signinRedirect(settings);
}

export async function processSigninResponse(url: string): Promise<string> {
  initialize();
  user = await userManager.signinRedirectCallback(url);
  cacheAccessToken(user.access_token);
  cacheIdToken(user.id_token);
  return user && user.state && user.state.redirectUrl;
}

export async function signout() {
  initialize();
  const request = await userManager.createSignoutRequest({
    id_token_hint: getIdToken()
  });
  if (!request || !request.url) {
    throw new Error('Unable to create signout request');
  }

  externalRedirect(request.url);
}

export async function processSignoutResponse() {
  initialize();
  clearCachedToken();
  await userManager.processSignoutResponse();
  user = undefined;
}
